// The page's side of analyze.worker.ts: the same calls as analyze.ts, run in a module worker when one starts, else on
// the main thread (still time-sliced). A worker that fails to load hands its pending runs to the main thread.
//   runAnalyze(file, cols, { signal?, onProgress? }) → Promise<Analysis>
//   runPortrait(file, cols, { signal?, onProgress? }) → Promise<Portrait>
import { abortError, type Portrait } from "./entropy";
import { analyzeFile, portraitOfFile, type Analysis } from "./analyze";
import type { WorkerReply, WorkerRequest } from "./analyze.worker";

type Opts = { signal?: AbortSignal; onProgress?: (p: number) => void };
type Pending = { op: "analyze" | "portrait"; file: File; cols: number; opts: Opts; resolve: (v: unknown) => void; reject: (e: unknown) => void };

let worker: Worker | null | undefined; // undefined: not tried yet; null: unavailable
let seq = 0;
const pending = new Map<number, Pending>();

function local(p: Pending) {
  const run = p.op === "analyze" ? analyzeFile(p.file, p.cols, p.opts) : portraitOfFile(p.file, p.cols, p.opts);
  run.then(p.resolve, p.reject);
}

function getWorker(): Worker | null {
  if (worker !== undefined) return worker;
  try {
    worker = new Worker(new URL("./analyze.worker.ts", import.meta.url), { type: "module", name: "malbox-analyze" });
  } catch {
    worker = null;
    return null;
  }
  worker.onmessage = (e: MessageEvent<WorkerReply>) => {
    const m = e.data;
    const p = pending.get(m.id);
    if (!p) return;
    if (m.type === "progress") return p.opts.onProgress?.(m.p);
    pending.delete(m.id);
    if (m.type === "done") p.resolve(m.result);
    else p.reject(Object.assign(new Error(m.message), { name: m.name }));
  };
  worker.onerror = (e) => {
    // the module failed to load or crashed: finish everything here instead
    e.preventDefault();
    worker?.terminate();
    worker = null;
    const left = [...pending.values()];
    pending.clear();
    left.forEach(local);
  };
  return worker;
}

function run<T>(op: "analyze" | "portrait", file: File, cols: number, opts: Opts): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    if (opts.signal?.aborted) return reject(abortError());
    const p: Pending = { op, file, cols, opts, resolve: resolve as (v: unknown) => void, reject };
    const w = getWorker();
    if (!w) return local(p);
    const id = ++seq;
    pending.set(id, p);
    opts.signal?.addEventListener(
      "abort",
      () => {
        if (!pending.delete(id)) return;
        w.postMessage({ id, op: "abort" } satisfies WorkerRequest);
        reject(abortError());
      },
      { once: true },
    );
    w.postMessage({ id, op, file, cols } satisfies WorkerRequest);
  });
}

export const runAnalyze = (file: File, cols: number, opts: Opts = {}) => run<Analysis>("analyze", file, cols, opts);
export const runPortrait = (file: File, cols: number, opts: Opts = {}) => run<Portrait>("portrait", file, cols, opts);
